import React, { useState, useEffect } from 'react';
import { Col, Row, Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import Button from 'components/inputs/Button';
import Input from 'components/inputs/Input';
import { RiWallet3Line } from 'react-icons/ri';
import { createTopUpOrder, verifyRazorpay, verifyTopUpOrder } from 'api/customer';
import { PAYMENT_GATEWAY, TOP_UP_INVOICE_STORAGE_KEY, TOP_UP_DONE_MESSAGE } from 'components/common/constant';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import * as _ from 'lodash';

const AddMoneyModal = ({ show, onHide, onSuccess }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  // Set once the order exists, the modal then shows the gateway page in an iframe
  const [juspayUrl, setJuspayUrl] = useState('');
  const [juspayLoaded, setJuspayLoaded] = useState(false);
  const [pendingInvoice, setPendingInvoice] = useState('');

  // walletPaymentStatus verifies inside the iframe and reports the result back here
  useEffect(() => {
    if (!pendingInvoice) {
      return undefined;
    }
    let stopped = false;
    const settle = (outcome) => {
      if (stopped) {
        return;
      }
      stopped = true;
      setPendingInvoice('');
      setJuspayUrl('');
      setJuspayLoaded(false);
      sessionStorage.removeItem(TOP_UP_INVOICE_STORAGE_KEY);
      if (outcome === 'paid') {
        toast.success('Money added successfully');
        onSuccess();
      } else if (outcome === 'failed') {
        toast.error('Payment failed — any amount debited will be refunded automatically.');
      } else if (outcome === 'pending') {
        toast.info('Your payment is being processed');
      }
      // cancelled means the user closed the modal without paying, nothing to report
      onHide();
    };
    const onMessage = (event) => {
      if (event.origin !== window.location.origin || _.get(event, 'data.type') !== TOP_UP_DONE_MESSAGE) {
        return;
      }
      settle(_.get(event, 'data.paid') ? 'paid' : _.get(event, 'data.outcome', 'pending'));
    };
    window.addEventListener('message', onMessage);
    // Fallback for gateway pages that never return to walletPaymentStatus
    const timer = setInterval(async () => {
      try {
        const res = await verifyTopUpOrder(pendingInvoice, {});
        const outcome = _.get(res, 'data.paid', false) ? 'paid' : _.get(res, 'data.outcome', 'pending');
        if (outcome === 'paid' || outcome === 'failed') {
          settle(outcome);
        }
      } catch {
        // Still in flight, the next tick tries again
      }
    }, 5000);
    return () => {
      stopped = true;
      window.removeEventListener('message', onMessage);
      clearInterval(timer);
    };
  }, [pendingInvoice, onHide, onSuccess]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (parseFloat(amount) > 500000) {
      toast.error('Amount exceeds maximum allowed payment limit of 5,00,000 INR');
      return;
    }

    setLoading(true);
    try {
      const orderRes = await createTopUpOrder({ amount: parseFloat(amount) });
      const { gateway, razorpay_order_id, razorpay_key, invoice_id, payment_link } = orderRes.data;

      // Juspay hosts the payment page, it renders inside this modal
      if (gateway === PAYMENT_GATEWAY.JUSPAY) {
        if (!payment_link) {
          toast.error('Failed to initiate payment');
          setLoading(false);
          return;
        }
        // Survives the top window navigation that netbanking and 3DS trigger
        sessionStorage.setItem(TOP_UP_INVOICE_STORAGE_KEY, invoice_id);
        setJuspayUrl(payment_link);
        setPendingInvoice(invoice_id);
        return;
      }

      const res = await loadRazorpayScript();
      if (!res) {
        toast.error('Razorpay SDK failed to load. Are you online?');
        setLoading(false);
        return;
      }

      const options = {
        key: razorpay_key,
        amount: parseFloat(amount) * 100,
        currency: 'INR',
        name: 'Charge Zone',
        description: 'Wallet Top-up',
        order_id: razorpay_order_id,
        handler: async (response) => {
          try {
            const verifyRes = await verifyRazorpay(invoice_id, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (verifyRes.status === 200) {
              toast.success('Money added successfully');
              onSuccess();
              onHide();
            }
          } catch (error) {
            if (!_.get(error, 'data.message')) {
              toast.error('Payment verification failed');
            }
          }
        },
        theme: {
          color: '#3399cc',
        },
      };

      const paymentObject = new window.Razorpay(options);
      onHide();
      paymentObject.open();
    } catch (error) {
      if (!_.get(error, 'data.message')) {
        toast.error('Failed to initiate payment');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setAmount('');
    setLoading(false);
    setJuspayUrl('');
    setJuspayLoaded(false);
    setPendingInvoice('');
  };

  // While paying, the modal shows the gateway page and nothing else
  if (juspayUrl) {
    return (
      <Modal show={show} onHide={onHide} onExited={resetState} centered size="lg" className="add-money-modal">
        {!juspayLoaded && <div className="p-5 text-center">Loading payment page...</div>}
        <iframe
          src={juspayUrl}
          title="Payment"
          onLoad={() => setJuspayLoaded(true)}
          style={{
            width: '100%',
            height: 'calc(100vh - 60px)',
            border: 0,
            display: juspayLoaded ? 'block' : 'none',
          }}
        />
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={onHide} onExited={resetState} centered className="add-money-modal">
      <div className="logout-modal-main_page">
        <div className="logout-modal--inner">
          <div className="logout-modal---block_title">
            <div className="inner--block_title">
              <div className="title">
                <RiWallet3Line size={30} color="#fff" />
              </div>
              <p className="sub-title">Add Money to Wallet</p>
            </div>
          </div>
          <div className="logout-modal-input-box__block p-4">
            <Row className="mb-4">
              <Col lg={12}>
                <Input
                  label="Enter Amount (INR)"
                  placeholder="0.00"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </Col>
            </Row>
            <Row>
              <Col lg={6}>
                <Button className="logout-model--btn cancel--btn w-100" onClick={onHide}>
                  {t('button.cancel')}
                </Button>
              </Col>
              <Col lg={6}>
                <Button className="logout-model--btn remove--btn w-100" onClick={handlePayment} loading={loading} disabled={loading || !amount}>
                  Pay Now
                </Button>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </Modal>
  );
};

AddMoneyModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddMoneyModal;
